import React from 'react';
import PropTypes from 'prop-types';
import { Field, connect } from 'formik';
import { startCase, get } from 'lodash';
import { Col, FormFeedback, Row } from 'reactstrap';
import DateTime from 'react-datetime';
import moment from 'moment-timezone';
import classnames from 'classnames';
import { processValidate } from 'infrastructure/form';
import { SelectField } from 'components/Form';

import './DateTimeField.scss';

const processValue = (value, zone) =>
  value && (moment.isMoment(value) ? value : moment.tz(value, zone).utc());

const formatValue = (value, zone, type) => {
  if (!value) return '';
  const formatOptions = {
    date: 'YYYY/MM/DD',
    datetime: 'YYYY/MM/DD HH:mm',
    time: 'HH:mm',
  };
  const m = moment.isMoment(value) ? value : moment.tz(value, zone);
  return m.isValid()
    ? m.format(formatOptions[type] || formatOptions.datetime)
    : '';
};

const DateTimeField = props => {
  const {
    formik,
    label,
    name,
    onChange,
    type,
    validate,
    withTimezone,
    timezones,
    defaultTimezone,
    disabled,
    ...fieldProps
  } = props;

  const zone = get(formik.values, `${name}Timezone`) || defaultTimezone;

  const placeholderOptions = {
    date: 'Date',
    time: 'Time',
    datetime: 'Date and Time',
  };

  const formatOptions = {
    date: 'YYYY/MM/DD',
    datetime: 'YYYY/MM/DD',
    time: 'HH:mm',
  };

  const error = get(formik.errors, name);
  const touched = get(formik.touched, name);
  const showError = error && touched;
  const isValid = touched && !error;

  const updateValue = form => value => {
    form.setFieldValue(name, value);
    form.setFieldTouched(name, true);
    if (onChange) {
      onChange(value);
    }
  };

  return (
    <div className="form__form-group-input-wrap--error-above datetime-field">
      <Row className="datetime-row">
        <Col className="datetime-col">
          <Field
            {...fieldProps}
            name={name}
            type="date"
            render={({ form, field, ...p }) =>
              disabled ? (
                <input
                  type="text"
                  className="form-control"
                  value={formatValue(field.value, zone, type)}
                  placeholder={placeholderOptions[type]}
                  disabled
                  readOnly
                />
              ) : (
                <DateTime
                  closeOnSelect
                  {...p}
                  {...field}
                  value={processValue(field.value, zone)}
                  zone={zone}
                  onChange={updateValue(form)}
                  onBlur={updateValue(form)}
                  name={name}
                  dateFormat={type !== 'time' && formatOptions[type]}
                  timeFormat={
                    (type === 'time' || type === 'datetime') &&
                    formatOptions.time
                  }
                  displayTimeZone={zone}
                  inputProps={{
                    placeholder: placeholderOptions[type],
                    className: classnames({
                      'is-valid': isValid,
                      'is-invalid': showError,
                    }),
                  }}
                />
              )
            }
            validate={processValidate(
              label || startCase(name),
              validate,
              props
            )}
          />
        </Col>
        {withTimezone && type !== 'date' && (
          <Col sm="auto" className="datetime-col timezone-col">
            <SelectField
              name={`${name}Timezone`}
              options={[
                {
                  label: 'Local',
                  value: moment.tz.guess(),
                },
                ...timezones,
              ]}
              defaultValue={zone}
              isDisabled={disabled}
            />
          </Col>
        )}
      </Row>
      {showError && (
        <FormFeedback className="validation form__form-group-error" tag="small">
          {error}
        </FormFeedback>
      )}
    </div>
  );
};

DateTimeField.propTypes = {
  label: PropTypes.string,
  mode: PropTypes.oneOf(['single', 'multiple']),
  minDate: PropTypes.string,
  name: PropTypes.string,
  type: PropTypes.oneOf(['date', 'datetime', 'time']),
  validate: PropTypes.arrayOf(PropTypes.shape({})),
  withTimezone: PropTypes.bool,
  defaultTimezone: PropTypes.string,
  disabled: PropTypes.bool,
  timezones: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ),
};

DateTimeField.defaultProps = {
  label: '',
  mode: 'single',
  minDate: '',
  name: '',
  type: 'datetime',
  validate: [],
  withTimezone: true,
  defaultTimezone: 'America/New_York',
  disabled: false,
  timezones: [
    { label: 'UTC', value: 'utc' },
    { label: 'EST', value: 'America/New_York' },
    { label: 'CST', value: 'America/Chicago' },
    { label: 'MST', value: 'America/Denver' },
    { label: 'PST', value: 'America/Los_Angeles' },
  ],
};

export default connect(DateTimeField);
